export const workflowDefinition =
  "Agentic Plus uses one governed workflow: capture input, organize evidence, create strategy, produce creative, approve execution, deliver with context, and retain what was learned.";

export const growthLoop = [
  {
    title: "Observe",
    description: "Collect authorized market, brand, product, creative, and performance context.",
  },
  {
    title: "Understand",
    description: "Structure evidence into comparable patterns, gaps, and test opportunities.",
  },
  {
    title: "Remember",
    description: "Reuse Brand Brain, product facts, prior decisions, and experiment outcomes.",
  },
  {
    title: "Hypothesize",
    description: "Propose a testable creative change with fixed and varied elements.",
  },
  {
    title: "Create",
    description: "Generate brief-linked variants while preserving lineage and constraints.",
  },
  {
    title: "Approve",
    description: "Review facts, assets, cost, destination, and permission before execution.",
  },
  {
    title: "Distribute",
    description: "Deliver approved versions through scoped and reviewable workflows.",
  },
  {
    title: "Measure",
    description: "Connect authorized results back to the exact creative version and hypothesis.",
  },
  {
    title: "Learn",
    description: "Write a decision record and update the next mission without losing context.",
  },
];

export const governanceChecks = [
  {
    title: "Context scope",
    description:
      "The agent names the workspace, brand, product, campaign, account, and destination it is using.",
  },
  {
    title: "Evidence coverage",
    description:
      "Recommendations identify supporting facts, gaps, assumptions, and the evidence level.",
  },
  {
    title: "Product fidelity",
    description:
      "Review checks protect product names, claims, prices, offers, visuals, and landing-page consistency.",
  },
  {
    title: "Single-variable tests",
    description:
      "A variation identifies the hypothesis, fixed elements, varied elements, and forbidden changes.",
  },
  {
    title: "Dry Run and approval",
    description:
      "External actions begin with a reviewable plan, explicit human approval, and cost visibility.",
  },
  {
    title: "Read-back and undo",
    description:
      "Execution records provider state where available and preserves a compensation or stop path.",
  },
];

export const workflowSteps = [
  {
    id: "input",
    title: "Input",
    description:
      "A mission starts with a concrete business input, not a blank chat. Users can provide a product URL, growth objective, creative question, brand brief, or known performance issue.",
    inputs: [
      "Product URL",
      "Product facts",
      "Growth goal",
      "Brand guidelines",
      "Campaign question",
    ],
  },
  {
    id: "evidence",
    title: "Evidence",
    description:
      "The platform organizes what is known before recommending or generating anything. Verified product and brand facts remain separate from model interpretation.",
    outputs: [
      "Evidence Graph",
      "Brand and product facts",
      "Historical creative",
      "Authorized market signals",
      "Evidence gaps",
    ],
  },
  {
    id: "strategy",
    title: "Strategy",
    description:
      "Evidence becomes a standardized brief and, where useful, a single-variable test design with fixed, varied, and forbidden changes.",
    outputs: [
      "Creative brief",
      "Test hypothesis",
      "Fixed and varied elements",
      "Format plan",
      "Success signal",
    ],
  },
  {
    id: "production",
    title: "Production",
    description:
      "Approved briefs drive copy, image, and standard video production. Each output retains its version, brief, references, and creation context.",
    outputs: ["Copy candidates", "Images", "Standard video", "Version history", "Creative lineage"],
  },
  {
    id: "approval",
    title: "Approval",
    description:
      "People review brand fit, product fidelity, quality, spend, and external consequences. Approval is tied to the specific plan and version, not a vague instruction.",
    outputs: [
      "Asset decision",
      "Spend confirmation",
      "Dry Run record",
      "Delivery authorization",
      "Approval record",
    ],
  },
  {
    id: "delivery",
    title: "Delivery",
    description:
      "Approved versions can be downloaded for a destination or passed through a controlled delivery workflow, with the destination, provider state, and result recorded.",
    outputs: [
      "Approved files",
      "Destination record",
      "External ID where available",
      "Delivery state",
      "Undo or stop path",
    ],
  },
  {
    id: "learning",
    title: "Learning",
    description:
      "After review or authorized performance feedback, the mission records what to repeat, revise, or stop. The next mission starts with that context.",
    outputs: [
      "Post-review",
      "Hypothesis outcome",
      "Decision Journal entry",
      "Experiment Memory",
      "Next mission input",
    ],
  },
];
